import React, { useEffect, useState, useMemo } from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
  Button,
  Chip,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider,
  Badge,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Description as DescriptionIcon,
  Assignment as TicketIcon,
  OpenInNew as OpenIcon,
  Phone as PhoneIcon,
  Add as AddIcon,
  FilterList as FilterIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  Sort as SortIcon,
  MoreVert as MoreIcon,
  EventNote as FristIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { adressenApi } from '../services/api';
import type { Adresse } from '../types/api';
import AdresseDetailDialog from '../components/AdresseDetailDialog';

interface KundeStats {
  anzahlEmails: number;
  anzahlDokumente: number;
  anzahlTicketsOffen: number;
  anzahlTicketsErledigt: number;
  fristenOffen: number;
  fristenUeberfaellig: number;
  fristenDieseWoche: number;
  naechsteFrist: string | null;
}

type FilterType = 'alle' | 'mitFristen' | 'ueberfaellig' | 'dieseWoche' | 'offeneTickets';
type SortType = 'name' | 'kundenNr' | 'datum' | 'fristen';

const Kunden: React.FC = () => {
  const navigate = useNavigate();
  const [kunden, setKunden] = useState<Adresse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState<Record<number, KundeStats>>({});
  const [loadingStats, setLoadingStats] = useState<Record<number, boolean>>({});
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedAdresseId, setSelectedAdresseId] = useState<number | null>(null);
  const [filter, setFilter] = useState<FilterType>('alle');
  const [sortBy, setSortBy] = useState<SortType>('name');
  const [sortAsc, setSortAsc] = useState(true);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [actionMenuKunde, setActionMenuKunde] = useState<Adresse | null>(null);

  useEffect(() => {
    loadKunden();
  }, []);

  const loadKunden = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adressenApi.getMandanten();
      setKunden(response.data);

      // Statistiken für alle Kunden laden
      response.data.forEach((kunde) => {
        loadKundeStats(kunde.adresseID);
      });
    } catch (err: any) {
      console.error('Kunden-Fehler:', err);
      setError(err.response?.data?.message || 'Fehler beim Laden der Kunden');
    } finally {
      setLoading(false);
    }
  };

  const loadKundeStats = async (adresseId: number) => {
    try {
      setLoadingStats((prev) => ({ ...prev, [adresseId]: true }));
      const response = await adressenApi.getMandantStatistik(adresseId);
      // API-Antwort auf KundeStats mappen (mit Default-Werten für neue Felder)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data = response.data as any;
      const kundeStats: KundeStats = {
        anzahlEmails: data.anzahlEmails || 0,
        anzahlDokumente: data.anzahlDokumente || 0,
        anzahlTicketsOffen: data.anzahlTicketsOffen || 0,
        anzahlTicketsErledigt: data.anzahlTicketsErledigt || 0,
        fristenOffen: data.fristenOffen || 0,
        fristenUeberfaellig: data.fristenUeberfaellig || 0,
        fristenDieseWoche: data.fristenDieseWoche || 0,
        naechsteFrist: data.naechsteFrist || null,
      };
      setStats((prev) => ({ ...prev, [adresseId]: kundeStats }));
    } catch (err) {
      console.error(`Fehler beim Laden der Statistik für Kunde ${adresseId}:`, err);
    } finally {
      setLoadingStats((prev) => ({ ...prev, [adresseId]: false }));
    }
  };

  const handleOpenDetails = (adresseId: number) => {
    setSelectedAdresseId(adresseId);
    setDetailDialogOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailDialogOpen(false);
    setSelectedAdresseId(null);
  };

  const handleActionMenuOpen = (event: React.MouseEvent<HTMLElement>, kunde: Adresse) => {
    event.stopPropagation();
    setActionMenuAnchor(event.currentTarget);
    setActionMenuKunde(kunde);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
    setActionMenuKunde(null);
  };

  const handleQuickAction = (action: string) => {
    if (!actionMenuKunde) return;

    switch (action) {
      case 'call':
        if (actionMenuKunde.telefon) {
          window.location.href = `tel:${actionMenuKunde.telefon}`;
        }
        break;
      case 'email':
        if (actionMenuKunde.email) {
          window.location.href = `mailto:${actionMenuKunde.email}`;
        }
        break;
      case 'ticket':
        navigate(`/tickets?neu=true&adresseId=${actionMenuKunde.adresseID}`);
        break;
      case 'dokument':
        navigate(`/dokumente?neu=true&adresseId=${actionMenuKunde.adresseID}`);
        break;
    }
    handleActionMenuClose();
  };

  // Filtern und Sortieren
  const filteredAndSortedKunden = useMemo(() => {
    let result = kunden.filter(
      (kunde) =>
        kunde.vollstaendigerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (kunde.firma || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (kunde.mandantNummer || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Filter anwenden
    if (filter !== 'alle') {
      result = result.filter((kunde) => {
        const kundeStats = stats[kunde.adresseID];
        if (!kundeStats) return false;

        switch (filter) {
          case 'mitFristen':
            return kundeStats.fristenOffen > 0;
          case 'ueberfaellig':
            return kundeStats.fristenUeberfaellig > 0;
          case 'dieseWoche':
            return kundeStats.fristenDieseWoche > 0;
          case 'offeneTickets':
            return kundeStats.anzahlTicketsOffen > 0;
          default:
            return true;
        }
      });
    }

    // Sortieren
    result.sort((a, b) => {
      let comparison = 0;
      const statsA = stats[a.adresseID];
      const statsB = stats[b.adresseID];

      switch (sortBy) {
        case 'name':
          comparison = a.vollstaendigerName.localeCompare(b.vollstaendigerName);
          break;
        case 'kundenNr':
          comparison = (a.mandantNummer || '').localeCompare(b.mandantNummer || '');
          break;
        case 'datum':
          const dateA = a.mandantSeit ? new Date(a.mandantSeit).getTime() : 0;
          const dateB = b.mandantSeit ? new Date(b.mandantSeit).getTime() : 0;
          comparison = dateB - dateA;
          break;
        case 'fristen':
          const fristenA = statsA?.fristenUeberfaellig || 0;
          const fristenB = statsB?.fristenUeberfaellig || 0;
          comparison = fristenB - fristenA;
          break;
      }

      return sortAsc ? comparison : -comparison;
    });

    return result;
  }, [kunden, searchTerm, filter, sortBy, sortAsc, stats]);

  // Gesamtstatistiken berechnen
  const totalStats = {
    anzahlKunden: kunden.length,
    anzahlEmails: Object.values(stats).reduce((sum, s) => sum + (s?.anzahlEmails || 0), 0),
    anzahlDokumente: Object.values(stats).reduce((sum, s) => sum + (s?.anzahlDokumente || 0), 0),
    anzahlTicketsOffen: Object.values(stats).reduce((sum, s) => sum + (s?.anzahlTicketsOffen || 0), 0),
    fristenOffen: Object.values(stats).reduce((sum, s) => sum + (s?.fristenOffen || 0), 0),
    fristenUeberfaellig: Object.values(stats).reduce((sum, s) => sum + (s?.fristenUeberfaellig || 0), 0),
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Kunden (Mandanten)</Typography>
        <Box display="flex" gap={2} alignItems="center">
          <TextField
            size="small"
            placeholder="Kunden suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Filter</InputLabel>
            <Select
              value={filter}
              label="Filter"
              onChange={(e) => setFilter(e.target.value as FilterType)}
              startAdornment={<FilterIcon sx={{ mr: 1, color: 'action.active' }} />}
            >
              <MenuItem value="alle">Alle Kunden</MenuItem>
              <MenuItem value="mitFristen">Mit Fristen</MenuItem>
              <MenuItem value="ueberfaellig">
                <Box display="flex" alignItems="center" gap={1}>
                  <WarningIcon color="error" fontSize="small" />
                  Überfällige Fristen
                </Box>
              </MenuItem>
              <MenuItem value="dieseWoche">Diese Woche fällig</MenuItem>
              <MenuItem value="offeneTickets">Offene Tickets</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Sortieren</InputLabel>
            <Select
              value={sortBy}
              label="Sortieren"
              onChange={(e) => setSortBy(e.target.value as SortType)}
              startAdornment={<SortIcon sx={{ mr: 1, color: 'action.active' }} />}
            >
              <MenuItem value="name">Name</MenuItem>
              <MenuItem value="kundenNr">Kunden-Nr.</MenuItem>
              <MenuItem value="datum">Kunde seit</MenuItem>
              <MenuItem value="fristen">Fristen</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Aktualisieren">
            <IconButton onClick={loadKunden} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/adressen')}
          >
            Neuer Kunde
          </Button>
        </Box>
      </Box>

      {/* Übersichts-Karten */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ py: 1.5 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <PersonIcon color="primary" />
                <Box>
                  <Typography variant="h5">{totalStats.anzahlKunden}</Typography>
                  <Typography variant="caption" color="text.secondary">Kunden</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card
            sx={{
              cursor: 'pointer',
              bgcolor: totalStats.fristenUeberfaellig > 0 ? 'error.light' : undefined,
              '&:hover': { bgcolor: totalStats.fristenUeberfaellig > 0 ? 'error.main' : 'action.hover' }
            }}
            onClick={() => setFilter(totalStats.fristenUeberfaellig > 0 ? 'ueberfaellig' : 'alle')}
          >
            <CardContent sx={{ py: 1.5 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <Badge badgeContent={totalStats.fristenUeberfaellig} color="error">
                  <FristIcon color={totalStats.fristenUeberfaellig > 0 ? 'error' : 'warning'} />
                </Badge>
                <Box>
                  <Typography variant="h5">{totalStats.fristenOffen}</Typography>
                  <Typography variant="caption" color="text.secondary">Fristen offen</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ py: 1.5 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <TicketIcon color="warning" />
                <Box>
                  <Typography variant="h5">{totalStats.anzahlTicketsOffen}</Typography>
                  <Typography variant="caption" color="text.secondary">Tickets offen</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ py: 1.5 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <EmailIcon color="info" />
                <Box>
                  <Typography variant="h5">{totalStats.anzahlEmails}</Typography>
                  <Typography variant="caption" color="text.secondary">E-Mails</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ py: 1.5 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <DescriptionIcon color="success" />
                <Box>
                  <Typography variant="h5">{totalStats.anzahlDokumente}</Typography>
                  <Typography variant="caption" color="text.secondary">Dokumente</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card sx={{ bgcolor: filter !== 'alle' ? 'primary.light' : undefined }}>
            <CardContent sx={{ py: 1.5 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <FilterIcon color={filter !== 'alle' ? 'primary' : 'action'} />
                <Box>
                  <Typography variant="h5">{filteredAndSortedKunden.length}</Typography>
                  <Typography variant="caption" color="text.secondary">Angezeigt</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Kunden-Tabelle */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Kunden-Nr.</TableCell>
              <TableCell>Name / Firma</TableCell>
              <TableCell>Kunde seit</TableCell>
              <TableCell align="center">Fristen</TableCell>
              <TableCell align="center">Tickets</TableCell>
              <TableCell align="center">E-Mails</TableCell>
              <TableCell align="center">Dokumente</TableCell>
              <TableCell align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAndSortedKunden.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="textSecondary" sx={{ py: 3 }}>
                    {filter !== 'alle'
                      ? 'Keine Kunden entsprechen dem gewählten Filter.'
                      : 'Keine Kunden vorhanden. Markieren Sie eine Adresse als Mandant, um sie hier anzuzeigen.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredAndSortedKunden.map((kunde) => {
                const kundeStats = stats[kunde.adresseID];
                const isLoadingStats = loadingStats[kunde.adresseID];

                return (
                  <TableRow
                    key={kunde.adresseID}
                    hover
                    onClick={() => handleOpenDetails(kunde.adresseID)}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: kundeStats?.fristenUeberfaellig > 0 ? 'error.lighter' : undefined,
                    }}
                  >
                    <TableCell>
                      <Chip
                        label={kunde.mandantNummer || '-'}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {kunde.firma ? (
                          <BusinessIcon fontSize="small" color="action" />
                        ) : (
                          <PersonIcon fontSize="small" color="action" />
                        )}
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {kunde.vollstaendigerName}
                          </Typography>
                          {kunde.firma && (
                            <Typography variant="caption" color="textSecondary">
                              {kunde.firma}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {kunde.mandantSeit
                          ? new Date(kunde.mandantSeit).toLocaleDateString('de-CH')
                          : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {isLoadingStats ? (
                        <CircularProgress size={16} />
                      ) : (
                        <Box display="flex" justifyContent="center" gap={0.5}>
                          {(kundeStats?.fristenUeberfaellig || 0) > 0 && (
                            <Tooltip title="Überfällig">
                              <Chip
                                icon={<WarningIcon />}
                                label={kundeStats?.fristenUeberfaellig}
                                size="small"
                                color="error"
                              />
                            </Tooltip>
                          )}
                          {(kundeStats?.fristenDieseWoche || 0) > 0 && (
                            <Tooltip title="Diese Woche fällig">
                              <Chip
                                icon={<ScheduleIcon />}
                                label={kundeStats?.fristenDieseWoche}
                                size="small"
                                color="warning"
                                variant="outlined"
                              />
                            </Tooltip>
                          )}
                          {!kundeStats?.fristenOffen && (
                            <Typography variant="body2" color="textSecondary">-</Typography>
                          )}
                          {kundeStats?.fristenOffen > 0 &&
                            !kundeStats?.fristenUeberfaellig &&
                            !kundeStats?.fristenDieseWoche && (
                              <Chip
                                label={kundeStats?.fristenOffen}
                                size="small"
                                color="default"
                                variant="outlined"
                              />
                            )}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {isLoadingStats ? (
                        <CircularProgress size={16} />
                      ) : (
                        <Box display="flex" justifyContent="center" gap={0.5}>
                          {(kundeStats?.anzahlTicketsOffen || 0) > 0 && (
                            <Chip
                              label={kundeStats?.anzahlTicketsOffen}
                              size="small"
                              color="warning"
                            />
                          )}
                          {(kundeStats?.anzahlTicketsErledigt || 0) > 0 && (
                            <Chip
                              label={kundeStats?.anzahlTicketsErledigt}
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          )}
                          {!kundeStats?.anzahlTicketsOffen && !kundeStats?.anzahlTicketsErledigt && (
                            <Typography variant="body2" color="textSecondary">-</Typography>
                          )}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {isLoadingStats ? (
                        <CircularProgress size={16} />
                      ) : (
                        <Typography variant="body2" color={kundeStats?.anzahlEmails ? 'inherit' : 'textSecondary'}>
                          {kundeStats?.anzahlEmails || '-'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {isLoadingStats ? (
                        <CircularProgress size={16} />
                      ) : (
                        <Typography variant="body2" color={kundeStats?.anzahlDokumente ? 'inherit' : 'textSecondary'}>
                          {kundeStats?.anzahlDokumente || '-'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Box display="flex" justifyContent="flex-end" gap={0.5}>
                        {kunde.telefon && (
                          <Tooltip title={`Anrufen: ${kunde.telefon}`}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => window.location.href = `tel:${kunde.telefon}`}
                            >
                              <PhoneIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {kunde.email && (
                          <Tooltip title={`E-Mail: ${kunde.email}`}>
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => window.location.href = `mailto:${kunde.email}`}
                            >
                              <EmailIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Mehr Aktionen">
                          <IconButton
                            size="small"
                            onClick={(e) => handleActionMenuOpen(e, kunde)}
                          >
                            <MoreIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Schnellaktionen-Menü */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleActionMenuClose}
      >
        <MenuItem onClick={() => handleOpenDetails(actionMenuKunde?.adresseID || 0)}>
          <ListItemIcon>
            <OpenIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Details anzeigen</ListItemText>
        </MenuItem>
        <Divider />
        {actionMenuKunde?.telefon && (
          <MenuItem onClick={() => handleQuickAction('call')}>
            <ListItemIcon>
              <PhoneIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Anrufen ({actionMenuKunde.telefon})</ListItemText>
          </MenuItem>
        )}
        {actionMenuKunde?.email && (
          <MenuItem onClick={() => handleQuickAction('email')}>
            <ListItemIcon>
              <EmailIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>E-Mail senden</ListItemText>
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={() => handleQuickAction('ticket')}>
          <ListItemIcon>
            <TicketIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Neues Ticket erstellen</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleQuickAction('dokument')}>
          <ListItemIcon>
            <DescriptionIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Neues Dokument erstellen</ListItemText>
        </MenuItem>
      </Menu>

      {/* Adresse Detail Dialog */}
      <AdresseDetailDialog
        open={detailDialogOpen}
        adresseId={selectedAdresseId}
        onClose={handleCloseDetails}
      />
    </Box>
  );
};

export default Kunden;
